import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/errorUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Mail, Phone, Shield } from 'lucide-react';
import { toast } from 'sonner';

const TeamPage = () => {
  const { user, isInternal } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTeam = useCallback(async () => {
    try {
      const response = await api.get('/users/my-team');
      setTeamMembers(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load team members'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const getRoleBadge = (role) => {
    const roleColors = {
      'ptc_admin': 'bg-purple-100 text-purple-800',
      'ptc_director': 'bg-indigo-100 text-indigo-800',
      'ptc_manager': 'bg-blue-100 text-blue-800',
      'ptc_team_member': 'bg-cyan-100 text-cyan-800',
      // v3.21-1634 · canonical external hierarchy
      'company_principal': 'bg-amber-100 text-amber-800',    // Principal
      'company_admin': 'bg-amber-100 text-amber-800',        // legacy Principal
      'company_director': 'bg-teal-100 text-teal-800',       // Director 1
      'manager_director': 'bg-emerald-100 text-emerald-800', // Manager Director
      'company_manager': 'bg-emerald-100 text-emerald-800',  // legacy Manager Director
      'company_team_member': 'bg-lime-100 text-lime-800'
    };
    return roleColors[role] || 'bg-slate-100 text-slate-800';
  };

  // v3.21-1634 · canonical Principal / Director 1 / Manager Director /
  // Team Member labels — every external Team display uses this map.
  const CANONICAL_ROLE_LABEL = {
    company_principal:   'Principal',
    company_admin:       'Principal',        // legacy alias
    company_director:    'Director 1',
    manager_director:    'Manager Director',
    company_manager:     'Manager Director', // legacy alias
    company_team_member: 'Team Member',
    ptc_admin:           'PTC Admin',
    ptc_director:        'PTC Director',
    ptc_manager:         'PTC Manager',
    ptc_team_member:     'PTC Team Member',
    ptc_principal_1:     'PTC Principal 1',
  };
  const formatRole = (role) => {
    if (CANONICAL_ROLE_LABEL[role]) return CANONICAL_ROLE_LABEL[role];
    return (role || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-primary">
          {isInternal() ? 'PTC Team' : 'My Team'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isInternal() 
            ? 'View all PTC team members' 
            : `Team members at ${user?.company_name || 'your company'}`
          }
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Members ({teamMembers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {teamMembers.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No team members found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="team-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-4 px-6 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((member) => (
                    <tr
                      key={member.id}
                      className="border-b border-slate-50 hover:bg-slate-50"
                      data-testid={`team-member-${member.id}`}
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white font-semibold">
                            {member.full_name?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{member.full_name}</p>
                            {member.id === user?.id && (
                              <span className="text-xs text-accent">(You)</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={getRoleBadge(member.role)} variant="secondary">
                          {formatRole(member.role)}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="h-4 w-4 text-slate-400" />
                          {member.email}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          {member.phone ? (
                            <>
                              <Phone className="h-4 w-4 text-slate-400" />
                              {member.phone}
                            </>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <Badge 
                          variant="secondary"
                          className={member.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                        >
                          {member.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamPage;
